// OfficePage — the pieces every Office records/insight/admin screen shares.
//
// Each screen renders INSIDE the DesktopShell's main area (the shell is owned
// by OfficeApp.js), so what lives here is page content only: a page header, a
// two-column split (table + detail aside), a panel card, a confirm sheet, and
// the fetch/format helpers. Nothing here defines a colour or a breakpoint —
// the tokens are ui/fw's (README §Tokens). Class prefix: `or-`.
//
// Data goes through apps/shared/http.js and nothing else. The two helpers that
// hand a file to the browser (`downloadBlob`, `downloadText`) use that module's
// API_BASE and authHeaders because `request()` parses JSON and a PDF is not.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { get, describeError, API_BASE, authHeaders, HttpError, NetworkError } from '../../shared/http';
import { usePageChrome } from '../../shell/chrome';
import { Notice, Pill, Sheet, FwButton, FwInput, formatHours, usePhoneLayout, useNarrowViewport } from '../../../ui/fw';
import { formatLocalDateShort } from '../../../utils/weekUtils';
import { formatDateTime } from '../../../utils/timezone';
import './OfficePage.css';
import { statusTone } from '../../shared/jobFields';
// Only the counting is shared with the other PageHeader — see the note there.
import { actionList, primaryIndexOf } from '../shared/Page';

// ── data ────────────────────────────────────────────────────────────────────

/**
 * useQuery over the shared http layer. `key` is the react-query key array.
 *
 * `queryFn` is an escape hatch for the one thing a bare `get(path)` cannot do:
 * stamp the answer with when it arrived. apps/shared/liveClock.js needs that —
 * a live elapsed clock computed from the device's own idea of "now" is wrong
 * by however wrong the device is — and `refetchInterval` is what makes the
 * answer live at all. Both default to today's behaviour, so no existing call
 * site changes.
 */
export function useOfficeQuery(key, path, { enabled = true, refetchInterval, queryFn } = {}) {
  return useQuery({
    queryKey: key,
    queryFn: queryFn || (() => get(path)),
    enabled,
    ...(refetchInterval ? { refetchInterval } : {}),
  });
}

/**
 * A busy flag plus the server's refusal sentence for any save/delete action.
 *   const { busy, error, run, clear } = useAction();
 *   run(async () => { await put(...); }) — resolves true on success, false on
 *   refusal (and sets `error` to a sentence a person can read).
 */
/**
 * Run one action, with a busy flag and a sentence.
 *
 * `describe` defaults to describeError, which maps 401 to "Your session
 * expired. Sign in again." That is right almost everywhere and WRONG where a
 * 401 is the answer rather than the session: the Security screen answers a
 * mistyped encryption passkey with 401 { error: 'Invalid passkey' }, and an
 * admin who fat-fingers it was told to sign in again. Pass `serverSentence`
 * there — it prefers what the server actually said.
 */
export function useAction(describe = describeError) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = useCallback(async (fn, fallback) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      return true;
    } catch (e) {
      setError(describe(e, fallback));
      return false;
    } finally {
      setBusy(false);
    }
  }, [describe]);
  const clear = useCallback(() => setError(''), []);
  return { busy, error, run, clear, setError };
}

/** Fetch a binary (the paystub PDF) with the shared token and hand it to the browser. */
export async function downloadBlob(path, filename) {
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, { headers: authHeaders() });
  } catch (e) {
    throw new NetworkError(path, e);
  }
  if (!res.ok) {
    let body = null;
    try { body = JSON.parse(await res.text()); } catch (_) { /* not json */ }
    throw new HttpError(res.status, body, path);
  }
  const blob = await res.blob();
  handToBrowser(blob, filename);
}

/** Build a text file (CSV) in memory and hand it to the browser. */
export function downloadText(text, filename, type = 'text/csv;charset=utf-8') {
  handToBrowser(new Blob([text], { type }), filename);
}

function handToBrowser(blob, filename) {
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * One CSV cell. Quotes anything with a comma, quote or newline, and disarms a
 * cell a spreadsheet would run as a formula.
 *
 * The second half is #160 §1's other hazard, carried over from the classic
 * Reports export (client/src/components/Reports/Reports.js:333) which is the
 * only other CSV writer in this repo. A cell beginning `=`, `+`, `-` or `@` is
 * EVALUATED when Excel or Google Sheets opens the file, and the surrounding
 * quotes do not prevent it — so a job title or a note typed on a phone is
 * executable content in a file that gets forwarded. A leading apostrophe is the
 * standard mitigation.
 *
 * Numbers are deliberately exempt: `-1.00` is an ordinary variance and appears
 * in most over-estimate rows, so testing only the first character would put an
 * apostrophe in front of a number all over the report.
 */
export function csvCell(v) {
  const raw = v === null || v === undefined ? '' : String(v);
  const isNumber = /^[-+]?\d*\.?\d+$/.test(raw);
  const s = /^[=+\-@]/.test(raw) && !isNumber ? `'${raw}` : raw;
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** One CSV line. Quotes anything with a comma, quote or newline. */
export function csvLine(values) {
  return values.map(csvCell).join(',');
}

// ── formatting ──────────────────────────────────────────────────────────────

const DASH = '—';

/** A DATE column (hire_date, due_date…): never handed to `new Date()` bare. */
export function fmtDate(dateOnly) {
  if (!dateOnly) return DASH;
  const s = String(dateOnly).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return String(dateOnly);
  return formatLocalDateShort(s, true);
}

/** A DATETIME column, rendered in the viewer's zone. */
export function fmtDateTime(ts) {
  if (!ts) return DASH;
  try { return formatDateTime(ts) || DASH; } catch (_) { return String(ts); }
}

/** Seconds → "8.25". Display only. */
export function hoursOf(seconds) {
  return formatHours((Number(seconds) || 0) / 3600);
}

/** A rate for a table cell. Display only; never used to compute pay. */
export function fmtRate(rate) {
  if (rate === null || rate === undefined || rate === '') return DASH;
  const n = Number(rate);
  return Number.isFinite(n) ? `$${n.toFixed(2)}/hr` : String(rate);
}

export function textOr(v) {
  return v === null || v === undefined || v === '' ? DASH : v;
}

/**
 * The first of `values` that is actually something, or `none` — never a dash.
 *
 * ── Why a card slot may not hold an em dash (2026-09-11) ────────────────────
 *
 * `textOr` is right for a TABLE cell: a row is scanned, the column header is
 * two inches above it, and a dash in one cell of nine reads as "not set" at a
 * glance. A CARD is not a narrower table. RecordList gives a record a title, a
 * subtitle, a badge and at most THREE facts (RECORD_FACT_LIMIT), and it picks
 * those slots from the COLUMN list — so a slot is spent per screen, not per
 * row. A column that is empty for this record still costs its line, and the
 * line says nothing.
 *
 * Measured in a browser at 320px on 2026-09-11, against seeded records with
 * the gaps a real book has:
 *
 *   /crew        every card's ONE fact read  Phone —      (no worker in the
 *                fixture had a phone_number, and GET /api/workers sends none
 *                for anybody added through Users)
 *   /properties  "Back paddock"              Address —
 *   /clients     "Bellamy Holdings"          subtitle was the dash itself
 *   /jobs        "Storm cleanup…"            subtitle was the dash itself
 *
 * A 129px crew card whose only fact is a dash is the owner's "nothing that
 * makes sense" stated precisely. So the rule these four screens now follow is:
 * fall through to the next thing that IS known, and when nothing is, say so in
 * words. A sentence is a fact ("No address on file"); a dash is a shrug.
 *
 * `none` is required rather than defaulted, because the right sentence is
 * different on every screen and a generic one would be the dash again with
 * more characters.
 */
export function factFallback(values, none) {
  const found = (values || []).find(
    (v) => v !== null && v !== undefined && String(v).trim() !== '' && String(v).trim() !== DASH,
  );
  return found === undefined ? none : found;
}

export const ROLE_TONE = { admin: 'sched', manager: 'warn', accounting: 'ok', worker: 'neutral' };

/**
 * Kept as a named export because six call sites import it from here; the answer
 * comes from the ONE map now.
 *
 * It used to be the switch below, and the switch was wrong in the way that is
 * hardest to see — it handled `in_progress`, `scheduled` and `pending`, which
 * this app has never had, and handled none of `todo`, `active` or `paused`,
 * which are three of its five real statuses (JOB_STATUSES, frozen server-side
 * at scheduling.js:12). All three fell to `default` and rendered the same
 * neutral grey on the Office Jobs list and detail.
 */
export const jobTone = statusTone;

/** Case-insensitive "any of these fields contains q". */
export function matches(row, fields, q) {
  const needle = (q || '').trim().toLowerCase();
  if (!needle) return true;
  return fields.some((f) => String(row[f] ?? '').toLowerCase().includes(needle));
}

// ── layout ──────────────────────────────────────────────────────────────────

/**
 * A screen's own header — and, on a phone, the shell's.
 *
 * Every Office screen already names itself here, so it feeds that name up
 * through usePageChrome rather than each screen wiring it separately. Without
 * this the phone header showed the DESTINATION label, which is the section
 * rather than the screen: /invoices/from-jobs said "Invoices" above a page
 * that said "Invoice from jobs", and /invoices/12 said "Invoices" above an
 * invoice number. Found in a browser at 390px, not in a unit test — the
 * desktop renders only the page's own header and looked right throughout.
 *
 * `meta` is deliberately not passed up: it is a count that belongs beside the
 * title, not a subtitle, and the shell's eyebrow is the section.
 *
 * ── On a phone the heading is the SHELL's, and only the shell's ─────────────
 *
 * Measured in a browser at 390px, 2026-09-10: every Office screen printed its
 * own name twice, one line under the other — the shell's `.fw-phone__title`
 * (fed by the usePageChrome call right below) and then this `<h1>` saying the
 * same word again. Nineteen screens, every role: /jobs read ["Jobs","Jobs"],
 * /invoices ["Invoices","Invoices"], /activity ["Activity log","Activity
 * log"], and so on. It cost ~55px at the top of a 780px phone screen — on
 * screens whose first record was already below the fold, Office Jobs putting
 * its first card at y=543 — and put two h1s in the accessibility tree.
 *
 * So on a phone this renders no heading. The meta and the actions stay: a
 * count and a New button are the PAGE's, not the shell's, and dropping the
 * whole header would take them with it. On desktop there is no shell header,
 * so nothing changes there. OfficePageHeader.test.js holds both directions.
 *
 * ── And on a phone the actions are ONE row, never a column ─────────────────
 *
 * The same treatment `office/shared/Page.js` got, in this header's own terms.
 * It is worth being precise about how much smaller the problem is here,
 * because the measurements are what decide whether this was worth doing.
 * Measured at 320x568 on 2026-09-11, against the built app:
 *
 *   /jobs          header  48px — one action, beside the meta. Correct.
 *   /clients       header  48px — same.
 *   /crew          header  20px — no actions at all.
 *   /settings      header  20px — no actions. (Its first panel is at y=676,
 *                  and the 531px `nav.ad-index` above it is what puts it
 *                  there. That is admin/Settings.js, not this header.)
 *   /activity      header  84px — ONE action, wrapped under a long meta.
 *   /notifications header 140px — "Mark all read" at y=122 and "Clear all"
 *                  at y=178, stacked one per line.
 *
 * So unlike the other header, this one does not stack today except on
 * /notifications. What it DOES have is the same latent failure: the actions
 * block wraps to a new line per button the moment a screen carries two, and
 * `.or-page__head`'s own comment records that two header buttons once ran
 * 33px past a 390px viewport and scrolled the page sideways.
 *
 * The collapse engages only at TWO OR MORE actions, so every 0- and 1-action
 * screen above renders exactly the DOM it did before — which is most of them,
 * and is why this change is small rather than dramatic.
 *
 * The two PageHeaders are deliberately NOT merged here; only `actionList` and
 * `primaryIndexOf` are shared, from `office/shared/Page.js`. They differ in
 * their prop (`children` vs `actions`), their class prefix and their title
 * rule (that one keeps a rich JSX heading on a phone; this one never has one),
 * and a second refactor stacked on an unmerged first is how a reviewable
 * change stops being reviewable.
 */
export function PageHeader({ title, meta, primaryAction, children }) {
  const phone = usePhoneLayout();
  const [moreOpen, setMoreOpen] = useState(false);
  usePageChrome(typeof title === 'string' ? title : '');

  const items = actionList(children);
  const collapse = phone && items.length > 1;
  const primaryIdx = collapse ? primaryIndexOf(items, primaryAction) : 0;
  const rest = collapse ? items.filter((_, i) => i !== primaryIdx) : [];

  // Widening past a phone takes the sheet with it, rather than leaving it
  // "open" over a row that is no longer collapsed.
  useEffect(() => { if (!collapse) setMoreOpen(false); }, [collapse]);

  // Close first, then run the screen's own handler — several of these open a
  // ConfirmSheet, and two sheets at once is a backdrop over a backdrop.
  const inSheet = (el, i) => {
    if (!React.isValidElement(el)) return <span key={`more-${i}`}>{el}</span>;
    const orig = el.props && el.props.onClick;
    return React.cloneElement(el, {
      key: el.key || `more-${i}`,
      onClick: (e) => { setMoreOpen(false); if (typeof orig === 'function') orig(e); },
    });
  };

  // `.fw-btn--full` is the class ui-check's "no control eats the screen" rule
  // exempts by name, and the primary spanning its row is the intent. Merged,
  // so a screen that passed its own className keeps it.
  const primaryEl = collapse && React.isValidElement(items[primaryIdx])
    ? React.cloneElement(items[primaryIdx], {
      className: [items[primaryIdx].props.className, 'fw-btn--full'].filter(Boolean).join(' '),
    })
    : items[primaryIdx];

  const titles = (
    <div className="or-page__titles">
      {phone ? null : <h1 className="or-page__title">{title}</h1>}
      {meta ? <p className="or-page__meta">{meta}</p> : null}
    </div>
  );

  return (
    <header className={collapse ? 'or-page__head or-page__head--collapsed' : 'or-page__head'}>
      {collapse ? (
        <div className="or-page__topline">
          {titles}
          <FwButton
            variant="ghost"
            icon="more"
            className="or-page__more-btn"
            onClick={() => setMoreOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={moreOpen ? 'true' : 'false'}
            aria-label={`${rest.length} more action${rest.length === 1 ? '' : 's'}`}
            title={`${rest.length} more action${rest.length === 1 ? '' : 's'}`}
            data-action="page-more"
            data-more-count={rest.length}
          />
        </div>
      ) : titles}
      {children ? (
        <div className={collapse ? 'or-page__actions or-page__actions--collapsed' : 'or-page__actions'}>
          {collapse ? primaryEl : children}
        </div>
      ) : null}
      {collapse ? (
        <Sheet
          open={moreOpen}
          title="More actions"
          onClose={() => setMoreOpen(false)}
          className="or-page__more"
        >
          <div className="or-page__more-list">{rest.map(inSheet)}</div>
        </Sheet>
      ) : null}
    </header>
  );
}

export function Toolbar({ children }) {
  return <div className="or-toolbar">{children}</div>;
}

/**
 * The filters a phone puts away until they are asked for.
 *
 * ── Why, measured at 390px on 2026-09-10 ───────────────────────────────────
 *
 * Office Jobs rendered a search box and FOUR labelled selects — status, crew,
 * tracking, created — before its first record, which began at y=543 of a 780px
 * viewport with a 68px tab bar under it. A person opening the app's main work
 * list saw one and a bit jobs beneath five empty controls. The Activity log
 * was worse: its events list started past y=885, so the screen a person opens
 * to read events showed no events at all.
 *
 * A filter is what you reach for when you cannot find something. It is not the
 * thing you came to look at, and it should not be what a phone shows first.
 *
 * Three rules, each of which is a failure this drawer could otherwise cause:
 *
 *   1. **Desktop renders NOTHING extra.** A toolbar row fits at 1280px, so
 *      there is nothing for a toggle to do there, and a control that does
 *      nothing is worse than no control (see DetailActions, same reasoning).
 *   2. **Closed means UNMOUNTED, not `display: none`.** CLAUDE.md rule 6
 *      records what a DOM-present, untappable control cost this repo — a probe
 *      that reported "opened the menu" against a hidden element. If a filter is
 *      not available, it is not there.
 *   3. **The toggle says how many are ON.** The failure a drawer invents is a
 *      person filtering, collapsing, forgetting, and then reading a short list
 *      as the whole list. `active` is the count of set filters and it is not
 *      optional in spirit — a drawer that hides a filter silently is worse
 *      than the crowded toolbar it replaced.
 *
 * `active` counts only the filters INSIDE the drawer. Whatever a screen keeps
 * out in the open (a search box, a status select) is visible anyway and would
 * be double-counted.
 */
export function MoreFilters({ active = 0, label = 'More filters', children }) {
  const phone = usePhoneLayout();
  const [open, setOpen] = useState(false);
  if (!phone) return children;
  return (
    <>
      <FwButton
        variant={active ? 'primary' : 'ghost'}
        icon={open ? 'chevronUp' : 'chevronDown'}
        className="or-toolbar__more"
        aria-expanded={open ? 'true' : 'false'}
        onClick={() => setOpen((v) => !v)}
      >
        {active ? `${label} · ${active} on` : label}
      </FwButton>
      {open ? <div className="or-toolbar__drawer">{children}</div> : null}
    </>
  );
}

/** How many of these values are set. What MoreFilters wants for `active`. */
export function countActive(...values) {
  return values.filter((v) => v !== null && v !== undefined && v !== '').length;
}

export function SearchBox({ value, onChange, placeholder = 'Search', label = 'Search' }) {
  return (
    <label className="or-search">
      <span className="fw-sr-only">{label}</span>
      <FwInput
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={label}
      />
    </label>
  );
}

export function Panel({ title, actions, children, className = '' }) {
  return (
    <section className={['or-panel', className].filter(Boolean).join(' ')}>
      {title || actions ? (
        <header className="or-panel__head">
          {title ? <h2 className="or-panel__title">{title}</h2> : <span />}
          {actions ? <div className="or-panel__actions">{actions}</div> : null}
        </header>
      ) : null}
      <div className="or-panel__body">{children}</div>
    </section>
  );
}

/**
 * The list and the opened record — side by side, or the record on its own.
 *
 * `full` UNMOUNTS the list rather than hiding it with CSS. A `display: none`
 * list still holds every row's click target and every toolbar control in the
 * accessibility tree, and this repo has already been bitten by a probe that
 * reported "opened the menu" against a control that could not be tapped
 * (CLAUDE.md rule 6). If the list is not available, it is not there.
 *
 * Default `false`, so the four admin screens and Crew that also render a Split
 * are untouched by this.
 */
export function Split({ main, aside, full = false }) {
  const wide = full && !!aside;
  return (
    <div className={['or-split', aside ? 'or-split--open' : '', wide ? 'or-split--full' : ''].filter(Boolean).join(' ')}>
      {wide ? null : <div className="or-split__main">{main}</div>}
      {aside ? <aside className="or-split__aside">{aside}</aside> : null}
    </div>
  );
}

// ── the opened record, and how much of the page it gets ─────────────────────

/**
 * Two search params, and nothing else, say what a records screen is showing:
 *
 *     ?open=<id>    which record's detail panel is open
 *     ?view=full    that detail has the whole page, not a 360px column
 *
 * Both live in the URL, and that is the whole point of this block. The owner's
 * report was *"it's nice that when working on things, some items open up a side
 * panel, but where's the full screen, kind of hard to always work on the side
 * panel"* — a job detail with six child panels squeezed into `flex: 0 1 360px`
 * while two thirds of a 1440px screen is a list they have stopped reading.
 *
 * Why the URL and not `useState`:
 *
 *   · A person working one record for twenty minutes reloads eventually — a
 *     deploy, a stale token, a dropped connection. State kept in a component
 *     puts them back at the top of a list.
 *   · "Look at this one" is a link. It could not be before.
 *   · "Go back to the split with this record still open" is not expressible
 *     if only half the state is addressable, which is why the SELECTED RECORD
 *     moved into the URL alongside the mode rather than staying in `useState`.
 *
 * `useSearchParams` is the mechanism these screens' neighbours already use for
 * exactly this (screens/Invoices.js, Estimates.js and Payroll.js all keep their
 * filter in a search param and write it with `{ replace: true }`), and a search
 * param rather than a new route is deliberate: `apps/oneApp.test.js` pairs
 * every route with a navigation destination in both directions, so a
 * `/jobs/:id` route would need an entry in the nav table for something that is
 * not a destination.
 */
export const RECORD_PARAM = 'open';
export const VIEW_PARAM = 'view';
export const FULL_VIEW = 'full';

/**
 * What a query string says is open, and how wide. Pure, so the two rules that
 * have consequences are testable without a router.
 *
 * `full` is false whenever nothing is open. `?view=full` on its own — a
 * hand-edited URL, or a link someone trimmed — would otherwise unmount the
 * list in favour of an aside that does not exist, which is a blank screen with
 * no control on it.
 */
export function readRecordView(search) {
  const params = new URLSearchParams(search || '');
  const openId = params.get(RECORD_PARAM) || null;
  return { openId, full: !!openId && params.get(VIEW_PARAM) === FULL_VIEW };
}

/**
 * The next query string. Pure for the same reason.
 *
 * Closing a record clears `view` as well as `open`: leaving it behind means
 * the NEXT record somebody opens lands full screen because a param from the
 * last one is still there, and every other param on the URL (a filter, a
 * status) is carried through untouched.
 */
export function nextRecordSearch(search, { id, full = false } = {}) {
  const next = new URLSearchParams(search || '');
  if (id === null || id === undefined || id === '') {
    next.delete(RECORD_PARAM);
    next.delete(VIEW_PARAM);
    return next;
  }
  next.set(RECORD_PARAM, String(id));
  if (full) next.set(VIEW_PARAM, FULL_VIEW);
  else next.delete(VIEW_PARAM);
  return next;
}

/**
 * The row the URL names, or null.
 *
 * String-compared on purpose: a search param is text and `jobs.id` is a
 * number, so `j.id === openId` is false for every row and the detail panel
 * silently never opens. That is the whole failure mode of moving state into a
 * URL, and it looks exactly like a list that ignores clicks.
 */
export function recordById(rows, openId) {
  if (openId === null || openId === undefined || openId === '') return null;
  return (rows || []).find((r) => r && String(r.id) === String(openId)) || null;
}

/**
 * The opened record and its width, read from and written to the URL.
 *
 * `open` keeps whatever width is current, so saving an edit from a full-screen
 * record does not throw the person back into the column they left.
 *
 * `toggleFull` PUSHES a history entry going into full screen and REPLACES one
 * coming out. That asymmetry is deliberate and is a second way back: the
 * platform Back gesture — the one every phone user already has, and the
 * browser button beside it — exits full screen, while a row click, a close, or
 * a collapse leaves no entry piling up behind it.
 */
export function useRecordView() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const search = params.toString();
  const { openId, full } = useMemo(() => readRecordView(search), [search]);

  // Did WE push the entry that is on screen? Only then is it ours to pop.
  // Arriving straight at `/jobs?open=42` from a link or a bookmark must not
  // let the close button walk somebody out of the application.
  const pushedRef = useRef(false);

  const go = useCallback((id, wide, push = false) => {
    setParams(nextRecordSearch(search, { id, full: wide }), { replace: !push });
    if (push) pushedRef.current = true;
  }, [setParams, search]);

  return {
    openId,
    full,
    /**
     * Opening a record PUSHES — once.
     *
     * This replaced, and that is the whole of the owner's complaint on
     * 2026-09-17: "every fucking time I click on something and try to go back,
     * it brings me to the main page of the tab and have to find the thing I
     * was working on again."
     *
     * Exactly right, and the mechanism is here. With a replace, `/jobs` never
     * existed as its own entry once a row was clicked — `/jobs?open=42` stood
     * in its place — so Back skipped the list entirely and went to whatever
     * came before it. On a phone that is the platform Back gesture, which is
     * the only way back most people use.
     *
     * The old comment defended it: "a row click, a close, or a collapse leaves
     * no entry piling up behind it." The concern is real — clicking through
     * ten rows should not make somebody press Back ten times — and the answer
     * is not to replace every time. It is to push only when nothing is open
     * yet, and replace while SWAPPING between records. So:
     *
     *   list -> row 42            push     Back returns to the list
     *   row 42 -> row 43 -> 44    replace  Back still returns to the list
     *
     * One entry for the whole excursion, and it goes where a person expects.
     */
    open: (id) => go(id, full, !openId),
    /**
     * Closing POPS the entry we pushed, rather than replacing it.
     *
     * A replace would leave the history as [list, list] — two identical
     * entries — so the first Back press after closing looks like it did
     * nothing. Popping leaves [list], which is the state the person was
     * actually in before they opened anything.
     *
     * Only when the entry is ours. If they arrived on an open record directly,
     * there is nothing of ours behind it and going back would leave the app,
     * so it clears the params instead.
     */
    close: () => {
      if (pushedRef.current) {
        pushedRef.current = false;
        navigate(-1);
        return;
      }
      go(null, false);
    },
    toggleFull: () => (full ? go(openId, false) : go(openId, true, true)),
  };
}

/**
 * The header controls on an opened record: make it the page, or close it.
 *
 * Discoverable without being noisy — it is a labelled button in the one place
 * a person already looks to get out of a detail panel, beside the close X,
 * rather than a gesture or an icon nobody hovers. The label is the owner's own
 * word for the thing they went looking for.
 *
 * On a NARROW viewport the expand control is not rendered at all, because
 * there is nothing for it to do: `.or-split__main` is `flex: 1 1 480px`, so
 * below 768px it already takes a whole row and the aside already takes the
 * next one at full width. A control that changes nothing is worse than no
 * control. It is not hidden with `display: none` either — a control in the DOM
 * that cannot be used is how a probe comes to report that it pressed
 * something.
 *
 * ── It asked usePhoneLayout() until 2026-09-16, and that was the wrong
 *    question ───────────────────────────────────────────────────────────────
 *
 * The premise above is about ROOM, and `usePhoneLayout` answers about the
 * DEVICE. The moment it learned that a phone in landscape is still a phone,
 * this control vanished from the two viewports where it does the most.
 * Measured on /jobs?open=1, coarse pointer:
 *
 *   390x844   aside 358 of a 358 row   full width
 *   844x390   aside 812 of an 812 row  full width
 *   600x900   aside 568 of a 568 row   full width
 *   956x440   aside 392 BESIDE a 512 main — it does not wrap at all
 *
 * ── Those are the SECOND set of numbers, and the first set's fate matters ──
 *
 * The 844x390 row read `aside 360px of an 812px row — 2.3x narrower`, and the
 * argument here was that hiding this button on a landscape phone lost a
 * control that widened the record 2.3x. True when measured; false eight
 * commits later, because the same PR gave `.or-split__aside` `flex-grow: 1`
 * inside the phone block and the aside now fills whatever line it lands on.
 * Caught in review. Both halves were mine and I did not connect them.
 *
 * So the ROOM premise now holds wherever the phone query matches — and the
 * reason this button still belongs at 844x390 is the OTHER thing `?view=full`
 * does: `Split` below UNMOUNTS the list, so full screen is the difference
 * between a record you scroll to and a record that IS the page. On a screen
 * 390px tall that is the bigger effect of the two.
 *
 * That reason would also apply at 390x844, where this still hides it. The old
 * premise answered that question and the old premise is dead; nobody has
 * measured what a crew member on a portrait phone wants. It is open, and
 * usePhoneLayout.js says so too rather than leaving the split looking settled.
 *
 * `useNarrowViewport` is the width question; usePhoneLayout.js says when each
 * is the right one to ask, and RecordList.js makes the same distinction
 * against a container.
 *
 * The EXIT is rendered at every width whenever `full` is set, because a state
 * you can arrive in by link must have a way out wherever you opened the link.
 */
export const FULL_LABEL = 'Full screen';
export const EXIT_FULL_LABEL = 'Exit full screen';

export function DetailActions({ full, onToggleFull, onClose }) {
  const narrow = useNarrowViewport();
  const label = full ? EXIT_FULL_LABEL : FULL_LABEL;
  return (
    <>
      {full || !narrow ? (
        // ── An icon, and the words on hover ──────────────────────────────
        //
        // Owner, 2026-09-18: "have the expand/open icon to click to make the
        // screen bigger or full screen - Don't label the button 'full screen'
        // looks stupid! have the icon and when hovering then show full screen".
        //
        // MEASURED at 1440px before this change: this button rendered 171px
        // wide carrying the text "FULL SCREEN", immediately beside a 44px
        // icon-only Close. Two controls doing the same KIND of job in two
        // different shapes, and the text one was four times the width, in a
        // header that also has to hold the record's title.
        //
        // `title` is the browser's own tooltip, which is what "show it on
        // hover" means without inventing a Tooltip primitive for one caller.
        // `aria-label` carries the same words for anyone not hovering — a
        // button whose only content is an svg has no accessible name at all
        // otherwise, and `title` alone is not dependable for that.
        //
        // The glyph is `expand`/`collapse` rather than the arrowLeft/arrowRight
        // pair it used to carry: with the label gone an arrow reads as "go
        // back" / "go forward", which is what arrows mean everywhere else in
        // this app.
        <FwButton
          variant="quiet"
          icon={full ? 'collapse' : 'expand'}
          onClick={onToggleFull}
          aria-pressed={full ? 'true' : 'false'}
          aria-label={label}
          title={label}
        />
      ) : null}
      <FwButton variant="quiet" icon="x" onClick={onClose} aria-label="Close detail" title="Close detail" />
    </>
  );
}

export function DetailRow({ label, children }) {
  return (
    <div className="or-detail__row">
      <dt className="or-detail__label">{label}</dt>
      <dd className="or-detail__value">{children}</dd>
    </div>
  );
}

export function DetailList({ children }) {
  return <dl className="or-detail">{children}</dl>;
}

export function ActivePill({ active }) {
  const on = isActive(active);
  return <Pill tone={on ? 'ok' : 'neutral'}>{on ? 'Active' : 'Inactive'}</Pill>;
}

/**
 * Is this row live? The one reading of `is_active`, which arrives as 1/0 from
 * SQLite, as true/false through some payloads, and as '1' from a form.
 */
export function isActive(value) {
  return value === 1 || value === true || value === '1';
}

/**
 * The Active / Inactive filter both Clients and Properties needed.
 *
 * ── Why (2026-09-11) ───────────────────────────────────────────────────────
 *
 * Both screens can DEACTIVATE a record — Clients' detail has Deactivate /
 * Reactivate, Properties' delete confirmation tells you to deactivate instead
 * "if crews might return" — and neither list could then filter by it. GET
 * /api/clients and /clients/properties/all both accept `active_only`, and
 * neither screen ever sent it, so a retired customer sat in the list between
 * two live ones with a grey pill as the only difference. On a phone, where the
 * list is cards, that is the owner's "cluster fuck … nothing that makes sense"
 * with a badge on it.
 *
 * Answered CLIENT-SIDE rather than by sending `active_only`, deliberately: the
 * same `['clients']` query key is shared with the Jobs screen's client picker
 * and with Clients' own properties panel, so narrowing the REQUEST would empty
 * a picker somewhere else on the strength of a filter set on another screen.
 * The rows are already in memory and neither list paginates.
 *
 * The default is '' — everything, exactly as today. A filter that starts
 * switched on hides records somebody could see yesterday, and nobody reads a
 * default. Narrowing is the person's choice to make.
 */
export const ACTIVE_VALUES = ['', 'active', 'inactive'];
export const ACTIVE_LABELS = {
  '': 'Active and inactive',
  active: 'Active only',
  inactive: 'Inactive only',
};

/** Does this row pass the Active filter? An empty filter passes everything. */
export function matchesActive(row, filter) {
  if (!filter) return true;
  return isActive(row && row.is_active) === (filter === 'active');
}

/**
 * Read one filter out of the query string, normalised against what the control
 * can actually OFFER.
 *
 * A <select> holding a value with no matching <option> paints the FIRST one,
 * so `?status=nonsense` would show "Active and inactive" above a list filtered
 * to nothing — a control displaying the opposite of what it is doing. Jobs.js
 * normalises every one of its params for this reason; this is that rule where
 * two screens can share it.
 */
export function readFilter(params, key, allowed) {
  const raw = (params && params.get(key)) || '';
  return allowed.includes(raw) ? raw : '';
}

export function LoadError({ error, fallback = 'Could not load this.' }) {
  if (!error) return null;
  return <Notice tone="stop" className="or-error">{describeError(error, fallback)}</Notice>;
}

export function FormError({ error }) {
  if (!error) return null;
  return <Notice tone="stop" className="or-form__error">{error}</Notice>;
}

// pickerNote moved to apps/shared/pickerNote.js on 2026-09-04, next to the
// form that needed it: apps/shared/JobForm.js is opened by Office, Dispatch
// and the schedule, and its header says it deliberately does not reach into
// Office's page helpers — so while this lived here, the one form every app
// opens to create a job was the only picker in the rebuild that could not tell
// an empty list from a refused one. Same move, same reason, as serverSentence
// out of apps/dispatch/lib/format.js the day before. Re-exported so every
// caller and OfficePageHelpers.test.js are untouched.
export { pickerNote } from '../../shared/pickerNote';

/** Field pairs on one line inside a Sheet form. */
export function FormRow({ children }) {
  return <div className="or-form__row">{children}</div>;
}

export function ConfirmSheet({ open, title, children, confirmLabel = 'Confirm', tone = 'stop', busy, onConfirm, onClose }) {
  return (
    <Sheet
      open={open}
      title={title}
      onClose={onClose}
      footer={(
        <div className="or-sheet__footer">
          <FwButton variant="ghost" onClick={onClose} disabled={busy}>Cancel</FwButton>
          <FwButton variant={tone === 'stop' ? 'stop' : 'primary'} onClick={onConfirm} loading={busy}>
            {confirmLabel}
          </FwButton>
        </div>
      )}
    >
      {children}
    </Sheet>
  );
}

export function SheetFooter({ onCancel, busy, submitLabel = 'Save', variant = 'primary', formId }) {
  return (
    <div className="or-sheet__footer">
      <FwButton variant="ghost" onClick={onCancel} disabled={busy}>Cancel</FwButton>
      <FwButton variant={variant} type="submit" form={formId} loading={busy}>{submitLabel}</FwButton>
    </div>
  );
}
